import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTeam } from '../contexts/TeamContext';
import { API_URL } from '../services/api';
import { useApiHeaders } from '../hooks/useApiHeaders';
import Layout from '../components/Layout';

interface SubscriptionStatus {
  has_subscription: boolean;
  customer_id: string | null;
  subscription_id: string | null;
  status: string;
}

export default function Billing() {
  const { firebaseUser } = useAuth();
  const { currentTeam } = useTeam();
  const { getHeaders } = useApiHeaders();
  const [subscription, setSubscription] = useState<SubscriptionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [openingPortal, setOpeningPortal] = useState(false);

  // Subscription configuration
  const [workspaceCount, setWorkspaceCount] = useState(1);
  const [seatCount, setSeatCount] = useState(1);
  const [billingInterval, setBillingInterval] = useState<'month' | 'year'>('month');
  const [creatingCheckout, setCreatingCheckout] = useState(false);

  // Subscription update state
  const [updatingSubscription, setUpdatingSubscription] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);

  // Stripe mode mismatch state
  const [stripeMismatch, setStripeMismatch] = useState(false);
  const [resettingAccount, setResettingAccount] = useState(false);

  // Minimum values based on current usage
  const minWorkspaces = Math.max(1, currentTeam?.license?.current_agents || 0);
  const minSeats = Math.max(1, currentTeam?.license?.current_seats || 0);

  // Pre-populate counts from current license when subscription exists
  useEffect(() => {
    if (currentTeam?.license) {
      setWorkspaceCount(Math.max(minWorkspaces, currentTeam.license.agent_limit || 1));
      setSeatCount(Math.max(minSeats, currentTeam.license.seat_limit || 1));
    }
  }, [currentTeam]);

  // Fetch subscription status
  useEffect(() => {
    async function fetchSubscription() {
      if (!firebaseUser) {
        setLoading(false);
        return;
      }

      try {
        const headers = await getHeaders();
        const res = await fetch(`${API_URL}/api/v1/billing/subscription`, {
          headers: headers,
        });

        if (res.ok) {
          const data = await res.json();
          setSubscription(data);
          if (data.code === 'stripe_mode_mismatch') {
            setStripeMismatch(true);
          }
        }
      } catch (error) {
        console.error('Failed to fetch subscription:', error);
      } finally {
        setLoading(false);
      }
    }

    fetchSubscription();
  }, [firebaseUser, currentTeam]);

  async function openCustomerPortal() {
    if (!firebaseUser) return;

    setOpeningPortal(true);
    try {
      const headers = await getHeaders();
      const res = await fetch(`${API_URL}/api/v1/billing/portal`, {
        headers: headers,
      });

      if (res.ok) {
        const { url } = await res.json();
        window.open(url, '_blank');
      } else {
        const data = await res.json();
        if (data.code === 'stripe_mode_mismatch') {
          setStripeMismatch(true);
        } else {
          alert(data.error || 'Failed to open billing portal');
        }
      }
    } catch (error) {
      console.error('Failed to open portal:', error);
      alert('Failed to open billing portal');
    } finally {
      setOpeningPortal(false);
    }
  }

  async function handleSubscribe() {
    if (!firebaseUser) return;

    setCreatingCheckout(true);
    try {
      const headers = await getHeaders();
      const res = await fetch(`${API_URL}/api/v1/billing/checkout`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({
          workspaceCount,
          seatCount,
          interval: billingInterval,
          email: firebaseUser.email,
        }),
      });

      if (res.ok) {
        const { url } = await res.json();
        window.location.href = url; // Redirect to Stripe Checkout
      } else {
        const error = await res.json();
        alert(error.error || 'Failed to create checkout session');
      }
    } catch (error) {
      console.error('Failed to create checkout:', error);
      alert('Failed to create checkout session');
    } finally {
      setCreatingCheckout(false);
    }
  }

  async function handleUpdateSubscription() {
    if (!firebaseUser) return;

    setUpdatingSubscription(true);
    setUpdateSuccess(false);
    try {
      const headers = await getHeaders();
      const res = await fetch(`${API_URL}/api/v1/billing/update-subscription`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({ workspaceCount, seatCount }),
      });

      if (res.ok) {
        setUpdateSuccess(true);
        setTimeout(() => setUpdateSuccess(false), 5000);
      } else {
        const data = await res.json();
        if (data.code === 'stripe_mode_mismatch') {
          setStripeMismatch(true);
        } else {
          alert(data.error || 'Failed to update subscription');
        }
      }
    } catch (error) {
      console.error('Failed to update subscription:', error);
      alert('Failed to update subscription');
    } finally {
      setUpdatingSubscription(false);
    }
  }

  async function handleResetAccount() {
    if (!firebaseUser) return;

    if (!confirm('This will remove your current subscription data and restore a 10-hour trial. Continue?')) {
      return;
    }

    setResettingAccount(true);
    try {
      const headers = await getHeaders();
      const res = await fetch(`${API_URL}/api/v1/billing/reset-subscription`, {
        method: 'POST',
        headers: headers,
      });

      if (res.ok) {
        setStripeMismatch(false);
        // Re-fetch subscription status
        const subRes = await fetch(`${API_URL}/api/v1/billing/subscription`, {
          headers: headers,
        });
        if (subRes.ok) {
          setSubscription(await subRes.json());
        }
      } else {
        const data = await res.json();
        alert(data.error || 'Failed to reset account');
      }
    } catch (error) {
      console.error('Failed to reset account:', error);
      alert('Failed to reset account');
    } finally {
      setResettingAccount(false);
    }
  }

  // Calculate total price
  function calculateTotal() {
    // Instance pricing (graduated)
    let workspaceCost = 0;
    if (workspaceCount <= 10) {
      workspaceCost = workspaceCount * 1.50;
    } else {
      workspaceCost = (10 * 1.50) + ((workspaceCount - 10) * 1.00);
    }

    // Seat pricing (flat)
    const seatCost = seatCount * 1.99;

    const monthlyTotal = workspaceCost + seatCost;

    if (billingInterval === 'year') {
      const yearlyTotal = monthlyTotal * 12 * 0.8; // 20% discount
      return {
        total: yearlyTotal,
        monthly: yearlyTotal / 12,
        savings: monthlyTotal * 12 - yearlyTotal,
      };
    }

    return {
      total: monthlyTotal,
      monthly: monthlyTotal,
      savings: 0,
    };
  }

  const price = calculateTotal();

  if (loading) {
    return (
      <Layout title="Billing" subtitle="Manage your subscription">
        <div className="flex items-center justify-center h-64">
          <div className="text-slate-600 dark:text-slate-400">Loading billing information...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Billing" subtitle="Manage your subscription">
      {/* Stripe Mode Mismatch Warning */}
      {stripeMismatch && (
        <div className="mb-6 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-xl p-6">
          <div className="flex items-start gap-3">
            <svg className="w-6 h-6 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-amber-800 dark:text-amber-300 mb-1">
                Stripe Mode Mismatch
              </h3>
              <p className="text-sm text-amber-700 dark:text-amber-400 mb-3">
                Your subscription was created in a different Stripe environment (test vs. live mode).
                This prevents billing operations from working. Reset your account to clear the stale
                data and restore a fresh 10-hour trial, then re-subscribe.
              </p>
              <button
                onClick={handleResetAccount}
                disabled={resettingAccount}
                className="px-5 py-2.5 bg-amber-600 hover:bg-amber-700 disabled:bg-amber-400 text-white font-semibold rounded-lg transition-colors"
              >
                {resettingAccount ? 'Resetting...' : 'Reset Account'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Current Subscription */}
      {subscription?.has_subscription ? (
        <div className="bg-white dark:bg-slate-900 shadow-sm rounded-2xl p-8 mb-8 border border-slate-200 dark:border-slate-800">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-semibold text-slate-900 dark:text-white mb-2">
                Active Subscription
              </h2>
              <p className="text-slate-600 dark:text-slate-400">
                Status:{' '}
                <span
                  className={`font-semibold ${
                    subscription.status === 'active'
                      ? 'text-green-600 dark:text-green-400'
                      : subscription.status === 'past_due'
                      ? 'text-yellow-600 dark:text-yellow-400'
                      : 'text-red-600 dark:text-red-400'
                  }`}
                >
                  {subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1)}
                </span>
              </p>
            </div>
          </div>

          {/* Usage Overview */}
          {currentTeam?.license && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              {/* AI Agents */}
              <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-5 border border-slate-200 dark:border-slate-700">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900/30 rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-500 dark:text-slate-400">AI Workspaces (current usage)</p>
                    <p className="text-2xl font-bold text-slate-900 dark:text-white">
                      {currentTeam.license.current_agents} <span className="text-base font-normal text-slate-400 dark:text-slate-500">/ {currentTeam.license.agent_limit}</span>
                    </p>
                  </div>
                </div>
                <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all ${
                      currentTeam.license.current_agents >= currentTeam.license.agent_limit
                        ? 'bg-red-500'
                        : currentTeam.license.current_agents >= currentTeam.license.agent_limit * 0.8
                        ? 'bg-yellow-500'
                        : 'bg-primary-500'
                    }`}
                    style={{ width: `${Math.min(100, (currentTeam.license.current_agents / currentTeam.license.agent_limit) * 100)}%` }}
                  />
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
                  {currentTeam.license.agent_limit - currentTeam.license.current_agents} remaining
                </p>
              </div>

              {/* Team Seats */}
              <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-5 border border-slate-200 dark:border-slate-700">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-10 h-10 bg-secondary-100 dark:bg-secondary-900/30 rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-secondary-600 dark:text-secondary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-500 dark:text-slate-400">Team Seats (current usage)</p>
                    <p className="text-2xl font-bold text-slate-900 dark:text-white">
                      {currentTeam.license.current_seats} <span className="text-base font-normal text-slate-400 dark:text-slate-500">/ {currentTeam.license.seat_limit}</span>
                    </p>
                  </div>
                </div>
                <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all ${
                      currentTeam.license.current_seats >= currentTeam.license.seat_limit
                        ? 'bg-red-500'
                        : currentTeam.license.current_seats >= currentTeam.license.seat_limit * 0.8
                        ? 'bg-yellow-500'
                        : 'bg-secondary-500'
                    }`}
                    style={{ width: `${Math.min(100, (currentTeam.license.current_seats / currentTeam.license.seat_limit) * 100)}%` }}
                  />
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
                  {currentTeam.license.seat_limit - currentTeam.license.current_seats} remaining
                </p>
              </div>
            </div>
          )}

          {/* Inline Quantity Editor */}
          <div className="border-t border-slate-200 dark:border-slate-700 pt-6">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
              Adjust Plan
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-4">
              <div>
                <label className="block text-sm font-semibold text-slate-900 dark:text-white mb-2">
                  AI Workspaces
                </label>
                <input
                  type="number"
                  min={minWorkspaces}
                  max="1000"
                  value={workspaceCount}
                  onChange={(e) => setWorkspaceCount(Math.max(minWorkspaces, parseInt(e.target.value) || minWorkspaces))}
                  className="w-full px-4 py-3 text-lg border-2 border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:border-slate-900 dark:focus:border-slate-400 transition-colors"
                />
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  Each workspace is one protected AI agent instance. $1.50/workspace (1-10) · $1/workspace (11+)
                  {minWorkspaces > 1 && <span className="block text-amber-600 dark:text-amber-400 mt-0.5">Minimum {minWorkspaces} (currently in use)</span>}
                </p>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-900 dark:text-white mb-2">
                  Team Seats
                </label>
                <input
                  type="number"
                  min={minSeats}
                  max="1000"
                  value={seatCount}
                  onChange={(e) => setSeatCount(Math.max(minSeats, parseInt(e.target.value) || minSeats))}
                  className="w-full px-4 py-3 text-lg border-2 border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:border-slate-900 dark:focus:border-slate-400 transition-colors"
                />
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  Each seat is one team member with console access. $1.99/seat
                  {minSeats > 1 && <span className="block text-amber-600 dark:text-amber-400 mt-0.5">Minimum {minSeats} (currently in use)</span>}
                </p>
              </div>
            </div>

            {/* Price Preview */}
            <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-4 mb-4">
              <div className="flex justify-between text-slate-700 dark:text-slate-300 text-sm mb-1">
                <span>{workspaceCount} Workspace{workspaceCount !== 1 ? 's' : ''}</span>
                <span className="font-medium">
                  ${((price.monthly / (workspaceCount + seatCount)) * workspaceCount).toFixed(2)}/mo
                </span>
              </div>
              <div className="flex justify-between text-slate-700 dark:text-slate-300 text-sm mb-2">
                <span>{seatCount} Seat{seatCount !== 1 ? 's' : ''}</span>
                <span className="font-medium">
                  ${((price.monthly / (workspaceCount + seatCount)) * seatCount).toFixed(2)}/mo
                </span>
              </div>
              <div className="border-t border-slate-300 dark:border-slate-700 pt-2 flex justify-between items-center">
                <span className="font-semibold text-slate-900 dark:text-white">Estimated monthly</span>
                <span className="text-lg font-bold text-slate-900 dark:text-white">${price.monthly.toFixed(2)}/mo</span>
              </div>
            </div>

            <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
              Changes are prorated on your next invoice.
            </p>

            {updateSuccess && (
              <div className="mb-4 px-4 py-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-400 text-sm font-medium">
                Subscription updated successfully! Changes will be reflected shortly.
              </div>
            )}

            <div className="flex items-center gap-3">
              <button
                onClick={handleUpdateSubscription}
                disabled={updatingSubscription || (currentTeam?.license && workspaceCount === currentTeam.license.agent_limit && seatCount === currentTeam.license.seat_limit)}
                className="px-6 py-3 bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700 disabled:from-slate-400 disabled:to-slate-500 text-white font-semibold rounded-lg transition-all shadow-md hover:shadow-lg disabled:shadow-none"
              >
                {updatingSubscription ? 'Updating...' : 'Update Subscription'}
              </button>
              <button
                onClick={openCustomerPortal}
                disabled={openingPortal}
                className="px-6 py-3 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50 transition-colors font-medium"
              >
                {openingPortal ? 'Opening...' : 'Payment & Invoices'}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Subscription Builder */}
          <div className="bg-white dark:bg-slate-900 shadow-sm rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-800">
            {/* Billing Interval Toggle */}
            <div className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-800 px-8 py-6">
              <div className="flex items-center justify-center gap-3">
                <button
                  onClick={() => setBillingInterval('month')}
                  className={`px-8 py-3 rounded-lg font-semibold transition-all ${
                    billingInterval === 'month'
                      ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900 shadow-md'
                      : 'bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-600'
                  }`}
                >
                  Monthly
                </button>
                <button
                  onClick={() => setBillingInterval('year')}
                  className={`px-8 py-3 rounded-lg font-semibold transition-all relative ${
                    billingInterval === 'year'
                      ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900 shadow-md'
                      : 'bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-600'
                  }`}
                >
                  Yearly
                  <span className="ml-2 inline-block px-2 py-0.5 bg-green-500 text-white text-xs rounded-full">
                    Save 20%
                  </span>
                </button>
              </div>
            </div>

            {/* Configuration */}
            <div className="p-8 space-y-8">
              {/* Bots */}
              <div>
                <label className="block text-sm font-semibold text-slate-900 dark:text-white mb-3">
                  AI Workspaces
                </label>
                <input
                  type="number"
                  min={minWorkspaces}
                  max="1000"
                  value={workspaceCount}
                  onChange={(e) => setWorkspaceCount(Math.max(minWorkspaces, parseInt(e.target.value) || minWorkspaces))}
                  className="w-full px-4 py-3 text-lg border-2 border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:border-slate-900 dark:focus:border-slate-400 transition-colors"
                />
                <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                  Each workspace is one protected AI agent instance. $1.50/workspace (1-10) · $1/workspace (11+)
                </p>
              </div>

              {/* Seats */}
              <div>
                <label className="block text-sm font-semibold text-slate-900 dark:text-white mb-3">
                  Team Seats
                </label>
                <input
                  type="number"
                  min={minSeats}
                  max="1000"
                  value={seatCount}
                  onChange={(e) => setSeatCount(Math.max(minSeats, parseInt(e.target.value) || minSeats))}
                  className="w-full px-4 py-3 text-lg border-2 border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:border-slate-900 dark:focus:border-slate-400 transition-colors"
                />
                <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                  Each seat is one team member with console access. $1.99/seat
                </p>
              </div>

              {/* Price Summary */}
              <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-6 space-y-3">
                <div className="flex justify-between text-slate-700 dark:text-slate-300">
                  <span>{workspaceCount} Workspace{workspaceCount !== 1 ? 's' : ''}</span>
                  <span className="font-medium">
                    ${((price.monthly / (workspaceCount + seatCount)) * workspaceCount).toFixed(2)}/mo
                  </span>
                </div>
                <div className="flex justify-between text-slate-700 dark:text-slate-300">
                  <span>{seatCount} Seat{seatCount !== 1 ? 's' : ''}</span>
                  <span className="font-medium">
                    ${((price.monthly / (workspaceCount + seatCount)) * seatCount).toFixed(2)}/mo
                  </span>
                </div>

                <div className="border-t border-slate-300 dark:border-slate-700 pt-3 mt-3">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-semibold text-slate-900 dark:text-white">
                      {billingInterval === 'year' ? 'Total per year' : 'Total per month'}
                    </span>
                    <span className="text-2xl font-bold text-slate-900 dark:text-white">
                      ${price.total.toFixed(2)}
                    </span>
                  </div>
                  {billingInterval === 'year' && (
                    <>
                      <div className="flex justify-between text-sm text-slate-600 dark:text-slate-400 mt-2">
                        <span>Effective monthly</span>
                        <span>${price.monthly.toFixed(2)}/month</span>
                      </div>
                      <div className="flex justify-between text-sm text-green-600 dark:text-green-400 font-medium mt-1">
                        <span>Annual savings</span>
                        <span>-${price.savings.toFixed(2)}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Subscribe Button */}
              <button
                onClick={handleSubscribe}
                disabled={creatingCheckout}
                className="w-full py-4 bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700 disabled:from-slate-400 disabled:to-slate-500 text-white text-lg font-semibold rounded-lg transition-all shadow-lg hover:shadow-xl disabled:shadow-none"
              >
                {creatingCheckout ? 'Loading...' : 'Subscribe Now'}
              </button>

              <p className="text-center text-sm text-slate-500 dark:text-slate-400">
                Secure payment powered by{' '}
                <a href="https://stripe.com" target="_blank" rel="noopener noreferrer" className="text-slate-900 dark:text-white hover:underline font-medium">
                  Stripe
                </a>
              </p>
            </div>
          </div>

          {/* Features */}
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white dark:bg-slate-900 p-6 rounded-xl border border-slate-200 dark:border-slate-800">
              <h3 className="font-semibold text-slate-900 dark:text-white mb-2">Enterprise Security</h3>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Prompt injection protection, credential security, and policy enforcement
              </p>
            </div>
            <div className="bg-white dark:bg-slate-900 p-6 rounded-xl border border-slate-200 dark:border-slate-800">
              <h3 className="font-semibold text-slate-900 dark:text-white mb-2">Complete Visibility</h3>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                90-day event retention, audit logs, and real-time monitoring
              </p>
            </div>
          </div>
        </>
      )}
    </Layout>
  );
}
